"use client";
import type { Page, Voice } from "@mw/shared/payload-types";

import dynamic from "next/dynamic";

import { type Agent, type Voice as ConvAIVoice, VOICES } from "./ConvAIHero/data";

const DeveloperDemo = dynamic(() => import("@components/demos/DeveloperDemo/DeveloperDemo"));
const DeveloperDemoSpeechToText = dynamic(() => import("@components/demos/DeveloperDemo/DeveloperDemoSpeechToText"));
const LanguagesPillGrid = dynamic(() => import("@components/demos/LanguagesPillGrid/LanguagesPillGrid"));
const ReaderAppHero = dynamic(() => import("@components/demos/ReaderAppHero/ReaderAppHero"));

const AudioNativeHero = dynamic(() => import("./AudioNativeHero/AudioNativeHero"));
const ConvAIAnatomy = dynamic(() => import("./ConvAIAnatomy/ConvAIAnatomy"));
const ConvAIHero = dynamic(() => import("./ConvAIHero/ConvAIHero"));
const DubbingHero = dynamic(() => import("./DubbingHero/DubbingHero"));
const ElevenStudiosAudience = dynamic(() => import("./ElevenStudiosAudience/ElevenStudiosAudience"));
const ElevenStudiosCreators = dynamic(() => import("./ElevenStudiosAudience/ElevenStudiosAudience"));
const ElevenStudiosCarousel = dynamic(() => import("./ElevenStudiosCarousel/ElevenStudiosCarousel"));
const ElevenStudiosTranslations = dynamic(() => import("./ElevenStudiosTranslations/ElevenStudiosTranslations"));
const ProjectsHero = dynamic(() => import("./ProjectsHero/ProjectsHero"));
const Safety = dynamic(() => import("./Safety/Safety"));
const UniversallyAccessibleContent = dynamic(
  () => import("./UniversallyAccessibleContent/UniversallyAccessibleContent"),
);
const VOStudioHero = dynamic(() => import("./VOStudioHero/VOStudioHero"));
const Voices = dynamic(() => import("./Voices/Voices"));
const VoicesConversational = dynamic(() => import("./VoicesConversational/VoicesConversational"));
const VoicesNarration = dynamic(() => import("./VoicesNarration/VoicesNarration"));
const ElevenLabsMobileAppHero = dynamic(() => import("./ElevenLabsMobileAppHero/index"));

interface DemoProps {
  className?: string;
  convAiDemoSettings?: Page["convAiDemoSettings"];
  demoKey: string;
}

export const AssetDemos: React.FC<DemoProps> = ({ demoKey, className }) => {
  switch (demoKey) {
    case "universallyAccessibleContent":
      return <UniversallyAccessibleContent />;
    case "elevenStudiosCarousel":
      return <ElevenStudiosCarousel />;
    case "elevenStudiosAudience":
      return <ElevenStudiosAudience />;
    case "elevenStudiosTranslations":
      return <ElevenStudiosTranslations />;
    case "elevenStudiosCreators":
      return <ElevenStudiosCreators />;
    case "developerDemoSpeechToText":
      return <DeveloperDemoSpeechToText />;
    case "safety":
      return <Safety />;
    case "voices":
      return <Voices className={className} />;
    case "projectsHero":
      return <ProjectsHero />;
    case "dubbingHero":
      return <DubbingHero />;
    case "readerAppHero":
      return <ReaderAppHero />;
    case "elevenLabsMobileAppHero":
      return <ElevenLabsMobileAppHero />;
    case "audioNativeHero":
      return <AudioNativeHero />;
    case "languagesDemo":
      return <LanguagesPillGrid />;
    case "voStudioHero":
      return <VOStudioHero />;
    case "convAIAnatomy":
      return <ConvAIAnatomy />;
    case "voicesNarration":
      return <VoicesNarration />;
    case "voicesConversational":
      return <VoicesConversational />;
    case "developerDemo":
      return <DeveloperDemo />;
    default:
      return null;
  }
};

export const HorizontalHeroDemos: React.FC<DemoProps> = ({ demoKey, convAiDemoSettings }) => {
  switch (demoKey) {
    case "convAIHero": {
      const { agents, voices } = convAiDemoSettings || {};
      const _agents: Agent[] | undefined =
        agents
          ?.filter(agent => agent.agentId && agent.name)
          .map(agent => ({
            agentId: agent.agentId,
            id: agent.id || agent.agentId,
            customName: agent.name,
          })) ?? undefined;
      const _voices: ConvAIVoice[] | undefined = Array.isArray(voices)
        ? voices
            .filter((voice): voice is Voice => typeof voice === "object" && "voiceId" in voice && "name" in voice)
            .map(voice => ({
              id: voice.id || voice.voiceId,
              customName: voice.name,
              voiceId: voice.voiceId,
              colors: VOICES[0].colors,
              orb: VOICES[0].orb,
            }))
        : undefined;

      return <ConvAIHero agents={_agents ?? undefined} voices={_voices ?? undefined} />;
    }
    default:
      return null;
  }
};
